import { Injectable, signal, computed } from '@angular/core';

export type ModalType = 'prompt' | 'confirm' | 'alert';

export interface ModalConfig {
  type: ModalType;
  title: string;
  message?: string;
  inputLabel?: string;
  inputValue?: string;
  inputPlaceholder?: string;
  confirmText?: string;
  cancelText?: string;
}

export interface ModalResult {
  confirmed: boolean;
  value?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private _isOpen = signal(false);
  private _config = signal<ModalConfig | null>(null);
  private _resolve: ((result: ModalResult) => void) | null = null;

  isOpen = computed(() => this._isOpen());
  config = computed(() => this._config());

  prompt(title: string, message?: string, defaultValue?: string): Promise<string | null> {
    return new Promise((resolve) => {
      this._config.set({
        type: 'prompt',
        title,
        message,
        inputValue: defaultValue || '',
        confirmText: 'Save',
        cancelText: 'Cancel'
      });
      this._isOpen.set(true);
      this._resolve = (result: ModalResult) => {
        resolve(result.confirmed ? (result.value ?? null) : null);
      };
    });
  }

  confirm(title: string, message?: string): Promise<boolean> {
    return new Promise((resolve) => {
      this._config.set({
        type: 'confirm',
        title,
        message,
        confirmText: 'Confirm',
        cancelText: 'Cancel'
      });
      this._isOpen.set(true);
      this._resolve = (result: ModalResult) => {
        resolve(result.confirmed);
      };
    });
  }

  alert(title: string, message?: string): Promise<void> {
    return new Promise((resolve) => {
      this._config.set({
        type: 'alert',
        title,
        message,
        confirmText: 'OK'
      });
      this._isOpen.set(true);
      this._resolve = () => {
        resolve();
      };
    });
  }

  close(result: ModalResult): void {
    this._isOpen.set(false);
    if (this._resolve) {
      this._resolve(result);
      this._resolve = null;
    }
    this._config.set(null);
  }
}
